import { COMPOUND_SUPPLY } from "@/services/mock";

export type CatalogOption = {
  id: string;
  label: string;
  isStock: boolean;
  conc: number;
  unit: "g" | "mL";
};

export function catalogFor(compoundId: string): CatalogOption[] {
  const supply = COMPOUND_SUPPLY[compoundId];
  const suppliers = (supply && supply.suppliers) || [];
  if (suppliers.length === 0) {
    return [
      { id: `${compoundId}-stock-a`, label: "Sigma · ≥99% (FCC)", isStock: false, conc: 1.0, unit: "g" },
      { id: `${compoundId}-stock-b`, label: "Bedoukian · 10% in EtOH", isStock: true, conc: 0.10, unit: "mL" },
    ];
  }
  return suppliers.slice(0, 4).map((s, i): CatalogOption => {
    const isStock = i % 3 === 1;
    const name = s.supplier || `Supplier ${i + 1}`;
    const size = s.size ? ` · ${s.size}` : "";
    return {
      id: `${compoundId}-${s.catalog || s.supplierId || i}`,
      label: `${name}${size} · ${isStock ? "10% in PG" : "neat"}`,
      isStock,
      conc: isStock ? 0.10 : 1.0,
      unit: isStock ? "mL" : "g",
    };
  });
}

export type TargetDose = { value: number; unit: "g" | "mg" | "mL" | "μL" };

// Compute target dose for a row given ppm + batch grams + selected catalog SKU.
// Stock solutions report as volume (mL/μL); neat reports as mass (g/mg).
export function computeTarget(
  ppm: number,
  batchG: number,
  catalog: CatalogOption
): TargetDose {
  const massG = (ppm / 1e6) * batchG;
  if (catalog.isStock) {
    const volMl = massG / catalog.conc;
    if (volMl < 1) return { value: volMl * 1000, unit: "μL" };
    return { value: volMl, unit: "mL" };
  }
  if (massG < 1) return { value: massG * 1000, unit: "mg" };
  return { value: massG, unit: "g" };
}

export function formatTarget(t: TargetDose): string {
  if (t.value >= 100) return t.value.toFixed(0);
  if (t.value >= 10) return t.value.toFixed(1);
  if (t.value >= 1) return t.value.toFixed(2);
  return t.value.toFixed(3);
}

export function fmtSheetStamp(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function fmtSheetDate(ts: number): string {
  const d = new Date(ts);
  return (
    d.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " · " +
    d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  );
}
